import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css, keyframes } from 'styled-components';
import ChevronLeftIcon from 'assets/chevron-left';
import ChevronRightIcon from 'assets/chevron-right';
import {
  addDays,
  parseYmd,
  safeLocale,
  toYmd,
  todayYmd,
} from './scheduleViewUtils';

interface DayStripProps {
  readonly selectedDate: string;
  readonly locale: string;
  readonly inventory: Record<string, boolean>;
  readonly onSelectDate: (date: string) => void;
}

type SlideDirection = 'forward' | 'backward';

// The strip is a fixed seven-day window. The prev/next arrows page the window by a week; popup
// picks realign the window so the selected day is always visible.
const WINDOW_DAYS = 7;
const MS_PER_DAY = 86400000;

interface DayDescriptor {
  readonly iso: string;
  readonly monthLabel: string;
  readonly weekdayLabel: string;
  readonly ariaLabel: string;
  readonly selectedAriaLabel: string;
}

interface DayCellProps {
  readonly day: DayDescriptor;
  readonly isActive: boolean;
  readonly hasInventory: boolean | undefined;
  readonly onSelect: (iso: string) => void;
}

// Memoised so a selection change reconciles only the two cells whose active flag flips.
const DayCell = React.memo(
  ({ day, isActive, hasInventory, onSelect }: DayCellProps) => {
    const handleClick = () => onSelect(day.iso);
    return (
      <DayCellButton
        aria-current={isActive ? 'date' : undefined}
        aria-label={isActive ? day.selectedAriaLabel : day.ariaLabel}
        aria-pressed={isActive}
        className={`schedule-view__day${
          isActive ? ' schedule-view__day--active' : ''
        }`}
        data-active={isActive}
        data-empty={hasInventory === false}
        onClick={handleClick}
        type="button"
      >
        <DayCellContent>
          <span className="schedule-view__day-month" aria-hidden="true">
            {day.monthLabel}
          </span>
          <span className="schedule-view__day-name" aria-hidden="true">
            {day.weekdayLabel}
          </span>
          {/* Slot always reserved (no layout shift); dot shows once inventory is known. */}
          <DayDots className="schedule-view__day-dots" aria-hidden="true">
            {hasInventory && <i />}
          </DayDots>
        </DayCellContent>
      </DayCellButton>
    );
  },
);
DayCell.displayName = 'DayCell';

const daysBetween = (fromIso: string, toIso: string): number =>
  Math.floor(
    (parseYmd(toIso).getTime() - parseYmd(fromIso).getTime()) / MS_PER_DAY,
  );

const DayStrip = ({
  selectedDate,
  locale,
  inventory,
  onSelectDate,
}: DayStripProps) => {
  const { t: translate } = useTranslation();
  // Stable onSelect so memoised cells aren't all invalidated when the parent's handler identity
  // changes (it depends on the URL params, which the scroll-spy rewrites as you scroll).
  const onSelectRef = useRef(onSelectDate);
  useEffect(() => {
    onSelectRef.current = onSelectDate;
  });

  // The anchor is today, except when a past deep-link is selected — then anchor to the selection
  // so the strip never hides it. The anchor is the earliest the window's left edge can go.
  const today = todayYmd();
  const anchorIso = selectedDate && selectedDate < today ? selectedDate : today;

  // The window's left edge. Clicking a cell only updates the selection — the strip stays put so
  // the clicked day keeps its position. The arrows page the window by a full week.
  const [windowStartIso, setWindowStartIso] = useState(anchorIso);
  // Direction of the most recent window shift — flips the slide-in keyframe so back-paging reads
  // as a right-to-left sweep. `null` means "no shift yet" and suppresses the animation entirely
  // so the strip doesn't slide in on initial mount.
  const [slideDirection, setSlideDirection] = useState<SlideDirection | null>(
    null,
  );
  // Stable read of the current window start so the memoised DayCells aren't invalidated on every
  // window change.
  const windowStartRef = useRef(windowStartIso);
  useEffect(() => {
    windowStartRef.current = windowStartIso;
  });

  // Realign the window when selectedDate falls outside it (popup picks, scroll-spy, mount with a
  // future deep-link). Intentionally NOT triggered when windowStartIso changes alone — that would
  // snap the strip back whenever the user pages via the prev/next arrows.
  const lastSelectedRef = useRef<string | null>(null);
  useEffect(() => {
    if (lastSelectedRef.current === selectedDate) return;
    lastSelectedRef.current = selectedDate;
    if (!selectedDate) return;
    const diff = daysBetween(windowStartRef.current, selectedDate);
    if (diff < 0 || diff >= WINDOW_DAYS) {
      setSlideDirection(diff < 0 ? 'backward' : 'forward');
      setWindowStartIso(selectedDate < anchorIso ? anchorIso : selectedDate);
    }
  }, [selectedDate, anchorIso]);

  const days = useMemo<ReadonlyArray<DayDescriptor>>(() => {
    const start = parseYmd(windowStartIso);
    const safe = safeLocale(locale);
    const monthFmt = new Intl.DateTimeFormat(safe, { month: 'short' });
    const weekdayFmt = new Intl.DateTimeFormat(safe, { weekday: 'short' });
    // Long-form date for the aria-label so screen readers announce the full localized date
    // (e.g. "Wednesday, June 25, 2026") rather than the abbreviated visible text.
    const ariaFmt = new Intl.DateTimeFormat(safe, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    return Array.from({ length: WINDOW_DAYS }, (_, i) => {
      const date = addDays(start, i);
      const ariaLabel = ariaFmt.format(date);
      return {
        iso: toYmd(date),
        monthLabel: `${monthFmt.format(date)} ${date.getDate()}`,
        weekdayLabel: weekdayFmt.format(date),
        ariaLabel,
        selectedAriaLabel: translate('selectedDateAriaLabel', {
          defaultValue: '{{date}}, selected',
          date: ariaLabel,
        }),
      };
    });
  }, [windowStartIso, locale, translate]);
  const handleSelect = useCallback((iso: string) => {
    onSelectRef.current(iso);
  }, []);

  const handlePrevPage = () => {
    setSlideDirection('backward');
    setWindowStartIso((prev) => {
      const next = toYmd(addDays(parseYmd(prev), -WINDOW_DAYS));
      return next < anchorIso ? anchorIso : next;
    });
  };
  const handleNextPage = () => {
    setSlideDirection('forward');
    setWindowStartIso((prev) => toYmd(addDays(parseYmd(prev), WINDOW_DAYS)));
  };

  const prevDisabled = windowStartIso <= anchorIso;

  return (
    <DaysNav
      className="schedule-view__days-nav schedule-view__day-strip"
      role="group"
      aria-label={translate('dateSelector', { defaultValue: 'Date selector' })}
    >
      <NavArrow
        type="button"
        className="schedule-view__nav-prev"
        aria-label={translate('previousWeek', {
          defaultValue: 'Previous week',
        })}
        disabled={prevDisabled}
        onClick={handlePrevPage}
      >
        <ChevronLeftIcon
          color="var(--way-colors-primaryTextColor)"
          scale={1.2}
        />
      </NavArrow>
      <Days className="schedule-view__days" slideDirection={slideDirection}>
        {days.map((day) => {
          const isActive = day.iso === selectedDate;
          // Key includes windowStartIso so every cell re-mounts on a window shift, retriggering
          // the slide-in animation for all seven cells in unison. Cheap: only 7 cells.
          return (
            <DayCell
              key={`${windowStartIso}-${day.iso}`}
              day={day}
              isActive={isActive}
              hasInventory={inventory[day.iso]}
              onSelect={handleSelect}
            />
          );
        })}
      </Days>
      <NavArrow
        type="button"
        className="schedule-view__nav-next"
        aria-label={translate('nextWeek', { defaultValue: 'Next week' })}
        onClick={handleNextPage}
      >
        <ChevronRightIcon
          color="var(--way-colors-primaryTextColor)"
          scale={1.2}
        />
      </NavArrow>
    </DaysNav>
  );
};

export default DayStrip;

/* ---- day strip ---- */
const DaysNav = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  /* Center the [prev chevron, Days group, next chevron] cluster inside DaysNav so the whole strip
     stays horizontally centred as the row width changes. Chevron-to-cell breathing room is
     applied as scaling inline margins on <Days> below (sharing the same container-query budget
     as the inter-cell gap), not as a fixed parent gap. */
  justify-content: center;
  gap: 0;
  min-width: 0;
  container-type: inline-size;

  /* Below 824px the row can't fit the picker + strip + filter side-by-side (even at 4px
     inter-cell gaps, DaysNav needs ~496px just for the arrows and 7 cells, which crowds the
     picker/filter on the edges). Hide the strip in this band — the calendar picker's dropdown
     remains available for navigation and is unaffected. */
  @media all and (max-width: 823px) {
    display: none;
  }
`;

const NavArrow = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  color: var(--way-colors-iconColor, var(--way-colors-primaryTextColor));
  flex: 0 0 auto;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const daySlideForward = keyframes`
  from { opacity: 0; transform: translateX(40px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const daySlideBackward = keyframes`
  from { opacity: 0; transform: translateX(-40px); }
  to   { opacity: 1; transform: translateX(0); }
`;

/* Stagger delays reversed on backward paging so the sweep visually flows from the right cell
   inward, matching the leftward direction of the incoming content. */
const backwardStagger = css`
  & > button:nth-child(1) {
    animation-delay: 108ms;
  }
  & > button:nth-child(2) {
    animation-delay: 90ms;
  }
  & > button:nth-child(3) {
    animation-delay: 72ms;
  }
  & > button:nth-child(4) {
    animation-delay: 54ms;
  }
  & > button:nth-child(5) {
    animation-delay: 36ms;
  }
  & > button:nth-child(6) {
    animation-delay: 18ms;
  }
  & > button:nth-child(7) {
    animation-delay: 0ms;
  }
`;

const Days = styled.div<{ slideDirection: SlideDirection | null }>`
  display: flex;
  /* Shrink-to-content so the day-cell group hugs its cells + gaps; DaysNav's flex-1 middle then
     centres the compact group inside the row instead of stretching cells to fill. */
  flex: 0 1 auto;
  align-items: stretch;
  gap: 8px;

  /* Gaps scale with DaysNav's own inline size (via container queries on DaysNav, above), not
     the viewport — so the strip yields room to the picker + filter whether the row is narrow
     because the browser is small or because the embed sits in a narrow column on a wide page.
     Budget: 7 cells × 56px min-width = 392px + ~80px prev/next arrows + 24px reserved for the
     two chevron-to-cell margins (max 12px each) = 496px non-gap. Anything above that in
     100cqw divides across the six inter-cell gaps here. The chevron-to-cell margins share the
     same budget and scale in lockstep so the strip contracts uniformly instead of squeezing
     one side first — inter-cell caps at 48px, chevron-to-cell caps at 12px. */
  @media all and (min-width: 824px) {
    gap: clamp(4px, calc((100cqw - 496px) / 6), 48px);
    margin-inline: clamp(0px, calc((100cqw - 496px) / 12), 12px);
  }

  /* When the window shifts (click-to-slide or arrow paging) every day cell re-mounts under a
     fresh key, so the slide-in runs across all seven cells. A long ease-out curve with a tight
     stagger reads as one cohesive sweep across the strip instead of seven separate pop-ins.
     The animation block is gated on slideDirection being non-null so the strip doesn't run
     the slide-in on initial mount (only after a user-initiated shift). */
  @media (prefers-reduced-motion: no-preference) {
    ${({ slideDirection }) =>
      slideDirection &&
      css`
        & > button {
          animation: ${slideDirection === 'backward'
              ? daySlideBackward
              : daySlideForward}
            480ms cubic-bezier(0.16, 1, 0.3, 1) both;
          will-change: transform, opacity;
        }
        & > button:nth-child(1) {
          animation-delay: 0ms;
        }
        & > button:nth-child(2) {
          animation-delay: 18ms;
        }
        & > button:nth-child(3) {
          animation-delay: 36ms;
        }
        & > button:nth-child(4) {
          animation-delay: 54ms;
        }
        & > button:nth-child(5) {
          animation-delay: 72ms;
        }
        & > button:nth-child(6) {
          animation-delay: 90ms;
        }
        & > button:nth-child(7) {
          animation-delay: 108ms;
        }
        ${slideDirection === 'backward' && backwardStagger}
      `}
  }
`;

const DayCellButton = styled.button`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 0px 0px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
  /* Content-sized, no flex-grow — combined with the min-width below every cell renders at the
     same visible width regardless of label length (e.g. "Jul 6" vs "Aug 22"). */
  flex: 0 0 auto;
  min-width: 56px;

  .schedule-view__day-month,
  .schedule-view__day-name {
    line-height: 1.2;
    color: var(--way-colors-tertiaryTextColor);
    font-variant-numeric: tabular-nums;
  }
  .schedule-view__day-month {
    font-size: 12px;
  }
  .schedule-view__day-name {
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    letter-spacing: 0.018px;
    font-size: 18px;
    font-family: inherit;
    padding-bottom: 2px;
    /* Transparent border acts as a spacer on inactive cells so heights don't jump when the
       active-state colour swaps in. */
    border-bottom: 0.5px solid transparent;
  }

  &[data-active='true'] .schedule-view__day-month,
  &[data-active='true'] .schedule-view__day-name {
    color: var(--way-colors-primaryTextColor);
  }
  &[data-active='true'] .schedule-view__day-name {
    border-bottom-color: var(--way-colors-borderHoverColor);
  }
  &[data-empty='true'] {
    opacity: 0.55;
  }
`;

/* Inline-flex wrapper around the day cell's labels. Sizes to the widest label inside so the
   labels stay horizontally centred inside the cell's 7-cell-share width. */
const DayCellContent = styled.span`
  align-items: center;
  display: inline-flex;
  flex-direction: column;
  gap: 8px;
`;

/* Inventory indicator beneath each day (slot always reserved to avoid layout shift). */
const DayDots = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  height: 6px;

  i {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: var(--way-colors-borderHoverColor);
    display: block;
  }
`;
